import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../_metronic/i18n/Metronici18n'

export type ConfirmTone = 'danger' | 'warning'

export interface ConfirmOptions {
  /** Already-localized title (call sites pass intl.formatMessage(...)). */
  title: string
  /** Optional already-localized second line. */
  body?: string
  /** Already-localized confirm button label. Defaults per language. */
  confirmText?: string
  /** Already-localized cancel button label. Defaults per language. */
  cancelText?: string
  /** Visual tone. Default 'danger'. */
  tone?: ConfirmTone
}

interface ActiveRequest {
  options: ConfirmOptions
  resolve: (result: boolean) => void
}

type Listener = () => void

let activeRequest: ActiveRequest | null = null
const listeners = new Set<Listener>()

function emit() {
  listeners.forEach((l) => l())
}

/** Imperative API: resolves true on confirm, false otherwise. */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  // Replace any in-flight request, resolving it as cancelled.
  if (activeRequest) {
    const prev = activeRequest
    activeRequest = null
    prev.resolve(false)
  }
  return new Promise<boolean>((resolve) => {
    activeRequest = { options, resolve }
    emit()
  })
}

/** Cancel the active dialog (Escape / backdrop / programmatic). */
export function dismissConfirm() {
  __resolveActive(false)
}

// ── internal helpers (also used by the host + tests) ──────────────────
export function __resolveActive(result: boolean) {
  if (!activeRequest) return
  const req = activeRequest
  activeRequest = null
  req.resolve(result)
  emit()
}

export function __getActiveRequest(): ActiveRequest | null {
  return activeRequest
}

export function __subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** React hook the host uses to re-render on store changes. */
export function useActiveConfirm(): ActiveRequest | null {
  const [, force] = useState(0)
  useEffect(() => __subscribe(() => force((n) => n + 1)), [])
  return activeRequest
}

// ── default button labels (no i18n key needed; "Confirm" key is absent) ──
const DEFAULT_LABELS: Record<'en' | 'fa', { confirm: string; cancel: string }> = {
  en: { confirm: 'Confirm', cancel: 'Cancel' },
  fa: { confirm: 'تایید', cancel: 'لغو' },
}

const TONE_GLYPH: Record<ConfirmTone, string> = {
  danger: '✕',
  warning: '!',
}

const readTheme = (): 'dark' | 'light' =>
  document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light'

export function LuminousConfirmHost() {
  const active = useActiveConfirm()
  const langCtx = useLang()
  const selectedLang = langCtx?.lang?.selectedLang === 'fa' ? 'fa' : 'en'
  const dir = langCtx?.lang?.dir === 'rtl' ? 'rtl' : 'ltr'

  const [theme, setTheme] = useState<'dark' | 'light'>(readTheme)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const lastFocused = useRef<Element | null>(null)

  // mirror app theme live
  useEffect(() => {
    const update = () => setTheme(readTheme())
    update()
    const observer = new MutationObserver(update)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    })
    return () => observer.disconnect()
  }, [])

  // Escape closes; lock body scroll; manage focus while open
  useEffect(() => {
    if (!active) return
    lastFocused.current = document.activeElement
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        __resolveActive(false)
      }
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    // focus Cancel after paint (safer default for destructive actions)
    const t = window.setTimeout(() => cancelRef.current?.focus(), 0)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      window.clearTimeout(t)
      if (lastFocused.current instanceof HTMLElement) lastFocused.current.focus()
    }
  }, [active])

  if (!active) return null

  const { options } = active
  const tone: ConfirmTone = options.tone ?? 'danger'
  const confirmText = options.confirmText ?? DEFAULT_LABELS[selectedLang].confirm
  const cancelText = options.cancelText ?? DEFAULT_LABELS[selectedLang].cancel

  return createPortal(
    <div
      className={`lc-overlay lc-theme-${theme}`}
      data-tone={tone}
      dir={dir}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) __resolveActive(false)
      }}
    >
      <div
        className='lc-card'
        role='alertdialog'
        aria-modal='true'
        aria-labelledby='lc-title'
        aria-describedby={options.body ? 'lc-body' : undefined}
      >
        <div className='lc-icon' aria-hidden='true'>
          <span className='lc-glyph'>{TONE_GLYPH[tone]}</span>
        </div>
        <h2 id='lc-title' className='lc-title'>
          {options.title}
        </h2>
        {options.body && (
          <p id='lc-body' className='lc-body'>
            {options.body}
          </p>
        )}
        <div className='lc-actions'>
          <button
            ref={cancelRef}
            type='button'
            className='lc-cancel'
            onClick={() => __resolveActive(false)}
          >
            {cancelText}
          </button>
          <button
            type='button'
            className='lc-confirm'
            onClick={() => __resolveActive(true)}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
